import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { analytics, WrapperStyles, getUsername } from "../../shared/utils";
import type { FBGroup, FBUser } from "../../shared/types";
import { fetchUser, fetchGroup, fetchGroupOwner } from "../../shared/services";
import DownloadOverlay from "../overlays/DownloadOverlay";
import FooterOverlay from "../overlays/FooterOverlay";
import LoadingOverlay from "../overlays/LoadingOverlay";
import DynamicMeta from "../shared/DynamicMeta";

const PublicGroupView = () => {
  const { id: groupId } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [group, setGroup] = useState<FBGroup>();
  const [user, setUser] = useState<FBUser>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch referrer id if passed
  const [searchParams] = useSearchParams();
  const referrerId = searchParams.get("referrerId");

  useEffect(() => {
    analytics.screen("Web/Public/Group", {
      groupId: groupId,
      referrerId: referrerId,
    });
  }, [groupId, referrerId]);

  useEffect(() => {
    if (!groupId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // Conditionally fetch user based on referrerId
    const userPromise = referrerId
      ? fetchUser(referrerId)
      : fetchGroupOwner(groupId);

    Promise.all([fetchGroup(groupId), userPromise])
      .then(([group, user]) => {
        setGroup(group);
        setUser(user);
      })
      .catch((error) => {
        console.error(error);
        setUser(undefined);
        setGroup(undefined);
      })
      .finally(() => setLoading(false));
  }, [groupId, referrerId]);

  const GroupOverlay = () => {
    if (!user || !group) return null;
    const name = getUsername(user);
    return (
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          gap: 1,
          width: "100% !important",
          borderRadius: 2,
        }}
      >
        <CardContent
          sx={{
            flex: "1 0 auto",
            flexDirection: "row",
            backgroundColor: "rgba(0, 0, 0, 0.87)",
            borderRadius: 2,
            width: "100% !important",
          }}
        >
          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {user.profilePicture && (
              <Avatar
                src={user.profilePicture}
                alt={name}
                sx={{ width: 48, height: 48 }}
              />
            )}
            <Typography
              sx={{
                color: "white",
                fontSize: !isMobile ? 18 : 16,
                fontWeight: "bold",
                textAlign: "center",
                numberOfLines: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {name} invited you to join their group
            </Typography>
            {group.groupAvatar && (
              <CardMedia
                component="img"
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 2,
                  objectFit: "cover",
                }}
                image={group.groupAvatar}
                alt="Group avatar"
              />
            )}
            {!group.groupAvatar && group.logo && (
              <CardMedia
                component="img"
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 2,
                  objectFit: "cover",
                }}
                image={group.logo}
                alt="Group logo"
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const getMetaTitle = () => {
    if (!group) return "Try Hooper today - download from the app store";
    const name = (user && getUsername(user)) || "Someone";
    return `${name} invited you to join ${group.name} on Hooper`;
  };

  const getMetaDescription = () => {
    if (!group) return "Create your stats and highlights, shot by shot";
    const name = (user && getUsername(user)) || "someone";
    return `Join ${name}'s group "${group.name}" on Hooper and connect with other basketball players in your community.`;
  };

  const getMetaImage = () => {
    if (group?.logo) return group.logo;
    if (user?.profilePicture) return user.profilePicture;
    return "/logos/logo-white.svg";
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: "100vh",
        flexDirection: "column",
        display: "flex",
        px: "0 !important",
      }}
    >
      <DynamicMeta
        title={getMetaTitle()}
        description={getMetaDescription()}
        image={getMetaImage()}
        type="profile"
        url={`/hoopers/group/${groupId}${referrerId ? `?referrerId=${referrerId}` : ""}`}
      />
      <Box sx={WrapperStyles}>
        <Box style={{ marginTop: 100 }} />
        {loading ? (
          <LoadingOverlay />
        ) : (
          <DownloadOverlay
            parentViewName="Group"
            backgroundId="background-4.jpg"
            deepLinkPath={`/hoopers/group/${groupId}${referrerId ? `?referrerId=${referrerId}` : ""}`}
            children={<GroupOverlay />}
          />
        )}
        <Box sx={{ my: 8 }} />
        <FooterOverlay />
      </Box>
    </Container>
  );
};

export default PublicGroupView;
